import {
  Alert,
  Button,
  Input,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
} from "antd";
import { ColumnsType } from "antd/es/table";
import { PlusOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import {
  getCityPricing,
  parseInternationalPricingError,
} from "../../services/internationalPricingApi";
import {
  CityPricing,
  CityPricingFilters,
} from "../../shared/dtos/internationalPricingDTO";
import CityPricingForm from "./CityPricingForm";
import { PRICE_TYPE_OPTIONS, REGION_OPTIONS } from "./utils";

const formatUsd = (value?: number | null) =>
  value == null
    ? "—"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(value);

const formatRange = (min?: number | null, max?: number | null) => {
  if (min == null && max == null) return "—";
  return `${formatUsd(min)} – ${formatUsd(max)}`;
};

const CityPricingTab = () => {
  const [rows, setRows] = useState<CityPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<CityPricingFilters>({
    page: 1,
    take: 20,
  });
  const [editingId, setEditingId] = useState<number>();
  const [formOpen, setFormOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const page = await getCityPricing(filters);
      setRows(page.data);
      setTotal(page.total);
    } catch (loadError) {
      setError(parseInternationalPricingError(loadError).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setFilter = (
    name: keyof CityPricingFilters,
    value: string | number | undefined,
  ) => {
    setFilters((current) => ({
      ...current,
      page: 1,
      [name]: value || undefined,
    }));
  };

  const columns: ColumnsType<CityPricing> = [
    { title: "País", dataIndex: "country", width: 140 },
    { title: "Ciudad", dataIndex: "city", width: 150 },
    {
      title: "Región",
      dataIndex: "regionKey",
      width: 140,
      render: (value) =>
        REGION_OPTIONS.find((option) => option.value === value)?.label || value,
    },
    {
      title: "Tipo",
      dataIndex: "priceType",
      width: 150,
      render: (value) => (
        <Tag color={value === "automatic" ? "blue" : "gold"}>
          {PRICE_TYPE_OPTIONS.find((option) => option.value === value)?.label}
        </Tag>
      ),
    },
    {
      title: "Aeropuerto USD",
      dataIndex: "airportUsd",
      width: 140,
      render: formatUsd,
    },
    {
      title: "USD/KM",
      width: 190,
      render: (_, row) => formatRange(row.usdPerKmMin, row.usdPerKmMax),
    },
    {
      title: "Tarifa mín",
      width: 190,
      render: (_, row) =>
        formatRange(row.minimumFareFloorUsd, row.minimumFareCeilingUsd),
    },
    {
      title: "Cobro mín",
      dataIndex: "minimumChargeUsd",
      width: 130,
      render: formatUsd,
    },
    {
      title: "Acción",
      fixed: "right",
      width: 90,
      render: (_, row) => (
        <Button
          type="link"
          onClick={() => {
            setEditingId(row.id);
            setFormOpen(true);
          }}
        >
          Editar
        </Button>
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setFilters((current) => ({
      ...current,
      page: pagination.current || 1,
      take: pagination.pageSize || 20,
    }));
  };

  return (
    <div className="international-pricing-tab">
      <div className="international-pricing-toolbar">
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Buscar país o ciudad"
            onSearch={(value) => setFilter("search", value.trim())}
          />
          <Input.Search
            allowClear
            placeholder="País"
            onSearch={(value) => setFilter("country", value.trim())}
          />
          <Input.Search
            allowClear
            placeholder="Ciudad"
            onSearch={(value) => setFilter("city", value.trim())}
          />
          <Select
            allowClear
            placeholder="Todas las regiones"
            options={REGION_OPTIONS}
            onChange={(value) => setFilter("regionKey", value)}
            style={{ minWidth: 170 }}
          />
          <Select
            allowClear
            placeholder="Todos los tipos"
            options={PRICE_TYPE_OPTIONS}
            onChange={(value) => setFilter("priceType", value)}
            style={{ minWidth: 180 }}
          />
        </Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingId(undefined);
            setFormOpen(true);
          }}
        >
          Agregar ciudad
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          action={
            <Button type="link" size="small" onClick={fetchRows}>
              Reintentar
            </Button>
          }
        />
      )}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1250 }}
        pagination={{
          current: filters.page,
          pageSize: filters.take,
          total,
          showSizeChanger: true,
          showTotal: (count) => `${count} ciudades`,
        }}
        onChange={handleTableChange}
      />

      <CityPricingForm
        open={formOpen}
        cityId={editingId}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          fetchRows();
        }}
      />
    </div>
  );
};

export default CityPricingTab;
